<template>
  <div class="editor-wrap rounded-lg">
    <!-- Toolbar -->
    <div class="mb-1 flex items-center justify-between gap-2 px-1 text-xs">
      <div class="text-base-content/70 flex items-center gap-2">
        <i class="fa-brands fa-windows"></i>
        <span class="font-semibold">PowerShell script</span>
        <span v-if="overLimit" class="text-warning">• Quá giới hạn</span>
      </div>
      <div class="flex items-center gap-3">
        <span :class="overLimit ? 'text-warning' : 'text-base-content/60'">
          {{ charCount }}/{{ maxChars }} ký tự · {{ lineCount }}/{{ maxLines }}
          dòng
        </span>
      </div>
    </div>

    <!-- Body: plain textarea. -->
    <div class="pane h-96 bg-[#1e1e1e]">
      <textarea
        ref="ta"
        aria-label="Commandline PowerShell"
        class="h-full w-full resize-none bg-transparent p-3 font-mono text-sm leading-[20px] text-gray-100 outline-none"
        spellcheck="false"
        autocapitalize="off"
        autocorrect="off"
        autocomplete="off"
        :value="model"
        :placeholder="placeholder"
        @input="onInput"
        @keydown="onKeydown"
      ></textarea>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import { toast } from "vue3-toastify";

const props = withDefaults(
  defineProps<{
    maxChars?: number;
    maxLines?: number;
    placeholder?: string;
  }>(),
  {
    modelValue: "",
    maxChars: 2000,
    maxLines: 150,
    placeholder: "Dán hoặc gõ script PowerShell vào đây...",
  },
);

const model = defineModel<string>({ default: "" });

const ta = ref<HTMLTextAreaElement | undefined>();

const charCount = computed(() => model.value.length);
const lineCount = computed(() => model.value.split("\n").length);
const overLimit = computed(
  () =>
    charCount.value > props.maxChars || lineCount.value - 1 > props.maxLines,
);

// Keep content within limits; trim from the end (and drop overflow lines) with a warning.
function clamp(v: string): string {
  const parts = v.split("\n");
  if (parts.length > props.maxLines + 1) {
    parts.splice(props.maxLines + 1);
    let rebuilt = parts.join("\n");
    while (rebuilt.length > props.maxChars) {
      const ls = rebuilt.split("\n");
      ls.pop();
      rebuilt = ls.join("\n");
    }
    model.value = rebuilt;
    toast.warn("Script dài quá giới hạn, đã tự cắt bớt.");
    return rebuilt;
  }
  if (v.length > props.maxChars) {
    const trimmed = v.slice(0, props.maxChars);
    model.value = trimmed;
    toast.warn("Script dài quá giới hạn, đã tự cắt bớt.");
    return trimmed;
  }
  return v;
}

function onInput(e: Event) {
  const target = e.target as HTMLTextAreaElement;
  model.value = clamp(target.value);
  nextTick(() => ta.value?.focus());
}

// Convert Tab into 2 spaces (PowerShell convention).
function onKeydown(e: KeyboardEvent) {
  if (e.key === "Tab") {
    e.preventDefault();
    const t = e.target as HTMLTextAreaElement;
    const s = t.selectionStart ?? model.value.length;
    const en = t.selectionEnd ?? model.value.length;
    model.value = model.value.slice(0, s) + "  " + model.value.slice(en);
    nextTick(() => {
      if (ta.value) {
        const ns = s + 2;
        ta.value.selectionStart = ta.value.selectionEnd = ns;
        ta.value.focus();
      }
    });
  }
}

defineExpose({ focus: () => ta.value?.focus(), value: () => model.value });
</script>
